// Custom DS-Table column for the Start / End date cells. When the opportunity has the date it shows the
// formatted value; when it is missing it shows the red "…date missing" label + a "?" help trigger that opens
// a popover (MissingDatePopover) explaining the missing date, with a primary "+ Add … Date" action. A plain
// DateColumn cannot express the missing-date CTA, so we author a column (dist-internal imports are the
// accepted web-front convention).
import React from 'react';
import type {ColumnDef} from '@tanstack/react-table';
// eslint-disable-next-line import/no-internal-modules -- established web-front convention for custom DS columns
import {defaultColumnDef} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/column/support/BaseColumn';
// eslint-disable-next-line import/no-internal-modules -- established web-front convention for custom DS columns
import {StyledCell, Align} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/cell/Cell.styled';
import type {OpportunitySortingFn, OpportunityTableRow} from '../opportunityTableRows';
import MissingDatePopover from './MissingDatePopover';
import Styled from 'styled-components';

// StyledCell sets `white-space: nowrap` and gives a cell no way to contain its content, so a
// value wider than the column spills over the neighbouring ones instead of clipping - the same
// failure the Quote Status pill had. The missing-date branch is contained inside
// MissingDatePopover instead, so that its label clips while the "?" trigger stays visible.
const DateCellContent = Styled.span`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	min-width: 0;
`;

export interface DateOrAddColumnType {
	id?: string;
	accessorKey: string; // the Date field (drives sorting)
	header: string;
	visible?: boolean;
	align?: Align;
	'data-cy'?: string;
	allowSorting?: boolean;
	sortingFn?: OpportunitySortingFn;
	width?: string;
	// The preformatted date label for this row, or null when the date is missing.
	dateLabel: (row: OpportunityTableRow) => string | null;
	// Opens the Add-Dates modal for the row (only reachable via the missing-date CTA).
	onAddDates: (row: OpportunityTableRow) => void;
	missingLabel: string; // the red inline label, e.g. "Start date missing"
	missingTitle: string; // popover title, e.g. "Start Date Missing"
	missingBody: string; // popover body copy
	missingActionLabel: string; // popover primary-button copy ("Add Date")
	closeLabel: string; // accessible label for the popover close button
	missingDataCy: string; // data-cy for the "?" trigger (add-start-date / add-end-date)
}

// Config-only component: DS Table consumes its props via toColumnDef and never renders it.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const DateOrAddColumn = (_: DateOrAddColumnType): null => null;

DateOrAddColumn.toColumnDef = (child: React.ReactElement<DateOrAddColumnType>): ColumnDef<OpportunityTableRow> => {
	const {
		accessorKey,
		sortingFn,
		dateLabel,
		onAddDates,
		missingLabel,
		missingTitle,
		missingBody,
		missingActionLabel,
		closeLabel,
		missingDataCy,
		align,
	} = child.props;
	return {
		...defaultColumnDef(child.props),
		accessorKey,
		sortingFn,
		cell: ({row}: {row: {original: OpportunityTableRow}}) => {
			const label = dateLabel(row.original);
			if (label) {
				return (
					<StyledCell align={align} data-cy="opportunity-date-cell">
						<DateCellContent>{label}</DateCellContent>
					</StyledCell>
				);
			}
			return (
				<StyledCell align={align} data-cy="opportunity-date-cell">
					<MissingDatePopover
						label={missingLabel}
						title={missingTitle}
						body={missingBody}
						actionLabel={missingActionLabel}
						closeLabel={closeLabel}
						triggerDataCy={missingDataCy}
						onAddDates={() => onAddDates(row.original)}
					/>
				</StyledCell>
			);
		},
	};
};

export default DateOrAddColumn;
